import { useParams, Link } from 'react-router-dom';
import { allArticles, categories } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';

const categoryNameMap: Record<string, string> = {};
const categoryDescMap: Record<string, string> = {};
categories.forEach((c) => {
  categoryNameMap[c.slug] = c.name;
  categoryDescMap[c.slug] = c.description;
});

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryName = currentCategory?.name || '分类';
  const articles = allArticles.filter((a) => a.category === slug);

  const pageTitle = `${categoryName} - 暗网51 | 深度解析与安全防护指南`;
  const pageDesc = currentCategory?.description || '暗网51分类文章列表';

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`暗网51,${categoryName},网络安全,暗网安全`}
        canonicalUrl={`https://www.darkweb51.com/category/${slug}`}
      />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-50 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: categoryName },
              ]}
            />
          </div>
        </div>

        <div className="bg-background-100 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center bg-accent-100 rounded-lg">
                <i className={`${currentCategory?.icon || 'ri-folder-line'} text-2xl text-accent-600`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  {categoryName}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">{currentCategory?.description}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-foreground-500 mr-2">分类导航：</span>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors no-underline ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-50 text-foreground-600 hover:bg-background-200 border border-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
          {articles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {articles.map((article) => (
                <ArticleCard
                  key={article.slug}
                  slug={article.slug}
                  title={article.title}
                  excerpt={article.excerpt}
                  category={article.category}
                  categoryName={categoryNameMap[article.category] || article.category}
                  author={article.author}
                  date={article.date}
                  readTime={article.readTime}
                  imageUrl={article.imageUrl}
                  tags={article.tags}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
                <i className="ri-file-search-line text-2xl text-foreground-400"></i>
              </div>
              <h3 className="text-lg font-semibold text-foreground-700 mb-2">暂无文章</h3>
              <p className="text-sm text-foreground-500">该分类下暂时没有文章，请稍后再来</p>
              <Link
                to="/"
                className="mt-4 inline-block px-4 py-2 bg-primary-500 text-white rounded-md text-sm font-medium hover:bg-primary-600 transition-colors no-underline"
              >
                返回首页
              </Link>
            </div>
          )}
        </section>
      </main>

      <Footer />
    </>
  );
}